import type { PbjPrediction } from "../../../utils/models";
import { SEQUENTIAL_BAND_EPSILON, WEEKEND_CATEGORY_ID } from "./cumulativeScoring.utils";
// SPEND_STEP is the slider's own granularity: two levels closer together than one step are the
// same level to a user, and the thumb is "on" a level when it is within half a step of it.
import { primaryScaleFor } from "./recMath.utils";
import { SPEND_STEP } from "./recommendationRow.utils";
import type { RecommendationType } from "./recommendations.constants";

/**
 * The spend values on a lever's slider where its points actually change — one per band edge the
 * lever can carry a category across, in the lever's own `$/applicable-PPD` units.
 *
 * The slider is continuous, but CMS scoring is a step function: between two edges a dollar buys
 * nothing. These levels are what the − / + buttons jump between and what the ticks mark, so a user
 * can reach the next point without hunting for the edge by dragging.
 *
 * Pure (no React, no I/O) — the inverse of the forward math in `recMath.utils::calculateRecData`,
 * so a level computed here and then fed through `calculateRecData` lands exactly on its band.
 */

// Spend that moves `category` by `shift` adjusted HPPD on this lever. Inverts recMath's
// `shift = spend × days(rec) × scale(cat) / (wage × scale(primary) × days(cat))` — census cancels
// out of both sides, so a level does not move when the census does.
const spendForCategoryShift = (
	shift: number,
	categoryId: string,
	rec: RecommendationType,
	wage: number,
	prediction: PbjPrediction,
	categoryScale: number,
): number | null => {
	const recDays = rec.isWeekend ? prediction.weekendDays : prediction.days;
	const categoryDays = categoryId === WEEKEND_CATEGORY_ID ? prediction.weekendDays : prediction.days;
	if (recDays <= 0 || categoryDays <= 0 || categoryScale <= 0 || wage <= 0) return null;
	return (shift * wage * primaryScaleFor(rec, prediction) * categoryDays) / (recDays * categoryScale);
};

type PointLevelsParams = {
	rec: RecommendationType;
	wage: number;
	prediction: PbjPrediction;
	// Per-category running HPPD the lever is measured from — the plan prefix before it, exactly the
	// baseline `rescoreRecAgainstPlan` scores the row's points against.
	baseline: Record<string, number>;
	// The lever's slider bounds; a level outside them is unreachable and is dropped.
	min: number;
	max: number;
};

/**
 * Every reachable point level for one lever, ascending. An edge ABOVE the baseline is offered at
 * the spend that lands just inside the next band (the same `+EPSILON` nudge the scorer applies, so
 * the level is credited rather than falling a float hair short). An edge BELOW it is offered at the
 * spend that lands exactly ON that floor — the deepest cut that still keeps the band, since a band
 * is `[min, max)` and its floor is inclusive.
 */
export const pointLevelSpends = ({ rec, wage, prediction, baseline, min, max }: PointLevelsParams): number[] => {
	const levels: number[] = [];
	for (const categoryId of rec.impactedIds) {
		const category = prediction.categories.find((c) => c.id === categoryId);
		// Only the HPPD ladders move with a lever; a measure the prediction dropped (N/A) has no edge.
		if (category?.kind !== "hppd" || !category.available) continue;
		const before = baseline[categoryId] ?? category.rawValue;
		for (const band of category.bands) {
			// The effective (safety-margin-buffered) bounds, because that is the ladder the score and
			// the optimizer were computed on. The band's lower bound is the edge that changes points.
			const edge = band.bufferedMinValue;
			if (edge === null) continue;
			const shift = edge - before + (edge > before ? SEQUENTIAL_BAND_EPSILON : 0);
			const spend = spendForCategoryShift(shift, categoryId, rec, wage, prediction, category.scale);
			if (spend === null || spend < min || spend > max) continue;
			levels.push(spend);
		}
	}
	// Two categories can cross at the same place; a user sees one tick there, not two.
	return levels.sort((first, second) => first - second).filter((spend, index, sorted) => index === 0 || spend - sorted[index - 1] > SPEND_STEP);
};

/** The nearest level strictly above `spend`, or `fallback` (the rail's end) when there is none left. */
export const levelAbove = (levels: number[], spend: number, fallback: number): number => levels.find((level) => level > spend + SPEND_STEP / 2) ?? fallback;

/** The nearest level strictly below `spend`, or `fallback` (the rail's end) when there is none left. */
export const levelBelow = (levels: number[], spend: number, fallback: number): number => {
	for (let index = levels.length - 1; index >= 0; index -= 1) {
		if (levels[index] < spend - SPEND_STEP / 2) return levels[index];
	}
	return fallback;
};
